import { ref, h } from 'vue'
import { getToken, onMessage, deleteToken } from 'firebase/messaging'
import { getFirebaseMessaging } from '@/config/firebase'
import { useNotificationStore } from '@/pinia-store/useNotificationStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useToast, POSITION } from 'vue-toastification'
import BellIcon from '@/assets/icons/bell.vue'
import Network from '@/Network'
import isSSR from '@/_base/isSSR'

const FCM_TOKEN_KEY = '@ME:fcm_token'
const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY || ''

export function usePushNotifications() {
  const isSupported = ref(false)
  const permissionStatus = ref<NotificationPermission>('default')
  const isRegistering = ref(false)

  function checkSupport(): boolean {
    if (isSSR) return false
    const supported = 'Notification' in window && 'serviceWorker' in navigator
    isSupported.value = supported
    return supported
  }

  async function requestPermission(): Promise<boolean> {
    if (isSSR) return false
    try {
      const permission = await Notification.requestPermission()
      permissionStatus.value = permission
      return permission === 'granted'
    } catch {
      return false
    }
  }

  async function registerAndSubscribe(): Promise<void> {
    if (isSSR || isRegistering.value) return
    isRegistering.value = true

    try {
      const messaging = getFirebaseMessaging()
      if (!messaging) return

      const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js')

      const currentToken = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swRegistration
      })

      if (!currentToken) return

      const storedToken = localStorage.getItem(FCM_TOKEN_KEY)
      if (currentToken !== storedToken) {
        const deviceInfo = getDeviceInfo()
        await Network.webNotification.subscribe(currentToken, deviceInfo)
        localStorage.setItem(FCM_TOKEN_KEY, currentToken)
      }

      setupForegroundHandler(messaging)
    } catch (e) {
      console.error('Erro ao registrar push notifications', e)
    } finally {
      isRegistering.value = false
    }
  }

  function setupForegroundHandler(messaging: ReturnType<typeof getFirebaseMessaging>): void {
    if (!messaging) return
    onMessage(messaging, (payload) => {
      const store = useNotificationStore()
      store.fetchUnreadCount()
      store.fetchNotifications()

      const toast = useToast()
      const title = payload.notification?.title || 'Nova notificação'
      toast(title, {
        icon: () => h(BellIcon, { color: '#2F7F91', width: '20', height: '20' }),
        toastClassName: 'toast-notification',
        position: POSITION.TOP_RIGHT,
        timeout: 5000,
        hideProgressBar: true,
        closeOnClick: true
      })
    })
  }

  async function unsubscribe(): Promise<void> {
    if (isSSR) return
    try {
      const storedToken = localStorage.getItem(FCM_TOKEN_KEY)
      if (storedToken) {
        await Network.webNotification.unsubscribe(storedToken).catch(() => {})
      }

      const messaging = getFirebaseMessaging()
      if (messaging) {
        await deleteToken(messaging).catch(() => {})
      }

      localStorage.removeItem(FCM_TOKEN_KEY)
    } catch {
      localStorage.removeItem(FCM_TOKEN_KEY)
    }
  }

  async function initialize(): Promise<void> {
    if (isSSR) return
    if (!checkSupport()) return

    const { flags } = useFeatureFlags()
    if (!flags.enabledWebPushNotifications) return

    permissionStatus.value = Notification.permission

    if (permissionStatus.value === 'granted') {
      await registerAndSubscribe()
    } else if (permissionStatus.value === 'default') {
      const granted = await requestPermission()
      if (granted) {
        await registerAndSubscribe()
      }
    }
  }

  function cleanup(): void {
    if (isSSR) return
    const storedToken = localStorage.getItem(FCM_TOKEN_KEY)
    if (storedToken) {
      Network.webNotification.unsubscribe(storedToken).catch(() => {})
    }
    localStorage.removeItem(FCM_TOKEN_KEY)
  }

  function getDeviceInfo(): string {
    if (isSSR) return ''
    return navigator.userAgent.substring(0, 200)
  }

  return {
    isSupported,
    permissionStatus,
    isRegistering,
    requestPermission,
    registerAndSubscribe,
    unsubscribe,
    initialize,
    cleanup
  }
}
